import api from '@/lib/api';
import type { PaginatedResponse } from '@pms/shared';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export type CompanyStatus = 'active' | 'inactive' | 'cancelled';
export type CompanyInvoiceStatus = 'draft' | 'issued' | 'paid' | 'overdue' | 'cancelled';

export interface Company {
  id: string;
  name: string;
  accountCode: string;
  companyType: string;
  directBillPrivileges: boolean;
  status: CompanyStatus;
  balance: string;
  creditLimit: string;
  paymentTerms: string;
  email: string | null;
  phone: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyInvoice {
  id: string;
  companyId: string;
  amount: string;
  status: CompanyInvoiceStatus;
  dueDate: string;
  bookingId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyDashboard {
  directBillLedger: string;
  accountsReceivable: string;
  totalInvoices: string;
  totalPayments: string;
  openInvoiceCount: number;
}

export interface CompaniesQuery {
  status?: string | undefined;
  directBill?: boolean | undefined;
  q?: string | undefined;
  propertyId?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface CreateCompanyPayload {
  name: string;
  accountCode: string;
  companyType?: string | undefined;
  directBillPrivileges?: boolean | undefined;
  paymentTerms?: string | undefined;
  creditLimit?: number | undefined;
  email?: string | undefined;
  phone?: string | undefined;
}

export interface UpdateCompanyPayload {
  name?: string | undefined;
  accountCode?: string | undefined;
  companyType?: string | undefined;
  directBillPrivileges?: boolean | undefined;
  status?: CompanyStatus | undefined;
  paymentTerms?: string | undefined;
  creditLimit?: number | undefined;
  email?: string | undefined;
  phone?: string | undefined;
}

export interface CompanyInvoicesQuery {
  status?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface CreateInvoicePayload {
  amount: string;
  dueDate: string;
  bookingId?: string | undefined;
  notes?: string | undefined;
}

export interface UpdateInvoicePayload {
  amount?: string | undefined;
  status?: CompanyInvoiceStatus | undefined;
  dueDate?: string | undefined;
}

// ─── Companies API ───────────────────────────────────────────────────────────

export async function fetchCompanies(query?: CompaniesQuery): Promise<PaginatedResponse<Company>> {
  const { data } = await api.get<PaginatedResponse<Company>>('/companies', { params: query });
  return data;
}

export async function createCompany(payload: CreateCompanyPayload): Promise<Company> {
  const { data } = await api.post('/companies', payload);
  return data;
}

export async function fetchCompany(id: string): Promise<Company> {
  const { data } = await api.get(`/companies/${id}`);
  return data;
}

export async function updateCompany(id: string, payload: UpdateCompanyPayload): Promise<Company> {
  const { data } = await api.patch(`/companies/${id}`, payload);
  return data;
}

export async function fetchCompanyDashboard(id: string): Promise<CompanyDashboard> {
  const { data } = await api.get(`/companies/${id}/dashboard`);
  return data;
}

// ─── Company Invoices API ────────────────────────────────────────────────────

export async function fetchCompanyInvoices(companyId: string, query?: CompanyInvoicesQuery): Promise<PaginatedResponse<CompanyInvoice>> {
  const { data } = await api.get<PaginatedResponse<CompanyInvoice>>(`/companies/${companyId}/invoices`, { params: query });
  return data;
}

export async function createCompanyInvoice(companyId: string, payload: CreateInvoicePayload): Promise<CompanyInvoice> {
  const { data } = await api.post(`/companies/${companyId}/invoices`, payload);
  return data;
}

export async function updateCompanyInvoice(
  companyId: string,
  invoiceId: string,
  payload: UpdateInvoicePayload,
): Promise<CompanyInvoice> {
  const { data } = await api.patch(`/companies/${companyId}/invoices/${invoiceId}`, payload);
  return data;
}
